// Modelos 3D: sensores I²C de la familia de entradas (MPU-6050 en GY-521, DS1307 en Tiny RTC y
// DS3231 en ZS-042).
import * as THREE from 'three';
import { Builder, M, mat, text, emissiveMat, setEmissive, glow, setGlow } from '../kit.js';
import { MPU, DS1307_BOARD, DS1307_P1, DS3231_BOARD } from '../../parts/inputs.js';
import { PITCH, SILK, pcb, pad, solder, headerUp, headerSide, smd, soic, sot23, label, chipTexture, sharedGeo } from './inputs-common.js';

/** LED SMD de encendido con su halo. */
function powerLed(root, x, z, y, color, glowColor) {
  const m = emissiveMat('#f1eee6', glowColor, { roughness: 0.3 });
  const led = new THREE.Mesh(sharedGeo('led0805', () => new THREE.BoxGeometry(2.0, 0.7, 1.25)), m);
  led.position.set(x, y + 0.35, z);
  root.add(led);
  const halo = glow(glowColor, 7);
  halo.position.set(x, y + 1.4, z);
  root.add(halo);
  return (on) => {
    setEmissive(m, on ? 1 : 0, 13);
    m.color.set(on ? color : '#f1eee6');
    setGlow(halo, on ? 0.8 : 0);
  };
}

// MPU-6050 (GY-521) ---------------------------------------------------------------------------------
function buildMpu6050() {
  const P = MPU;
  const b = new Builder();
  const top = P.top;
  const names = ['VCC', 'GND', 'SCL', 'SDA', 'XDA', 'XCL', 'AD0', 'INT'];
  const xs = names.map((_, i) => (i - 3.5) * PITCH);
  pcb(b, {
    key: 'gy521', w: P.w, d: P.d, y0: P.pcbY, color: '#1f58bb', edge: '#174592', scale: 28,
    holes: [[-8.1, -5.2, 1.5], [8.1, -5.2, 1.5]],
    draw: (g, X, Y) => {
      names.forEach((n, i) => text(g, n, X(xs[i]), Y(P.pinZ - 1.75), { size: 1.0, color: SILK, align: 'left', rotate: -Math.PI / 2, weight: 700 }));
      text(g, 'GY-521', X(0), Y(-6.6), { size: 1.45, color: SILK, weight: 700 });
      // Ejes del sensor: X a la derecha, Y hacia el lado opuesto al conector, Z hacia arriba.
      const ox = X(-6.2);
      const oy = Y(-0.6);
      g.strokeStyle = SILK;
      g.fillStyle = SILK;
      g.lineWidth = 0.22;
      g.beginPath();
      g.moveTo(ox, oy);
      g.lineTo(ox + 3.2, oy);
      g.moveTo(ox, oy);
      g.lineTo(ox, oy - 3.2);
      g.stroke();
      for (const [ax, ay, dx, dy] of [[ox + 3.6, oy, 1, 0], [ox, oy - 3.6, 0, -1]]) {
        g.beginPath();
        g.moveTo(ax, ay);
        g.lineTo(ax - dx * 0.8 + dy * 0.45, ay - dy * 0.8 + dx * 0.45);
        g.lineTo(ax - dx * 0.8 - dy * 0.45, ay - dy * 0.8 - dx * 0.45);
        g.fill();
      }
      g.beginPath();
      g.arc(ox, oy, 0.45, 0, Math.PI * 2);
      g.stroke();
      text(g, 'X', ox + 4.4, oy, { size: 1.0, color: SILK });
      text(g, 'Y', ox, oy - 4.3, { size: 1.0, color: SILK });
      text(g, 'LED', X(7.0), Y(1.4), { size: 0.8, color: SILK });
    },
  });
  // Conector de 8 pines hacia abajo (separador de plástico bajo la placa) y sus soldaduras.
  b.box(8 * PITCH, P.pcbY, PITCH, M.blackPlastic, 0, 0, P.pinZ);
  for (const x of xs) {
    solder(b, x, P.pinZ, top);
    b.box(0.64, 1.0, 0.64, M.gold, x, top, P.pinZ);
  }
  // MPU-6050 (QFN de 4 × 4 mm), regulador de 3.3 V y componentes pasivos
  b.box(4, 0.9, 4, M.chip, 0, top, -0.4);
  label(b.root, chipTexture(['MPU-6050', 'C1A23'], 4, 4), 4, 4, { y: top + 0.9, z: -0.4 });
  for (let i = 0; i < 6; i++) {
    for (const s of [-1, 1]) {
      b.box(0.25, 0.1, 0.5, M.tin, -1.25 + i * 0.5, top, -0.4 + s * 2.05);
      b.box(0.5, 0.1, 0.25, M.tin, s * 2.05, top, -1.65 + i * 0.5);
    }
  }
  sot23(b, -6.4, 3.0, top, { rot: 0 });
  const caps = [[-3.6, -3.2, 0], [3.4, -3.4, 0], [-3.6, 2.6, Math.PI / 2], [3.6, 2.4, Math.PI / 2], [-8.2, 0.2, Math.PI / 2], [5.6, -1.2, Math.PI / 2], [0.4, 3.5, 0]];
  caps.forEach(([x, z, r], i) => smd(b, x, z, top, { rot: r, w: 1.6, d: 0.8, color: i % 2 ? '#1b1b1d' : '#b59a72' }));
  smd(b, 8.4, 1.6, top, { rot: Math.PI / 2, color: '#1b1b1d' });
  b.build();
  const led = powerLed(b.root, 7.0, 3.0, top, '#7dff8a', '#36ff4a');
  led(false);
  return {
    root: b.root,
    update(v) {
      led(!!v.on);
    },
    reset() {
      led(false);
    },
  };
}

// Pila CR2032 en su portapilas, bajo la placa -------------------------------------------------------
function coinCell(b, x, z, pcbY, battery) {
  const holder = mat('#1d1e21', { roughness: 0.5 });
  // Pared del portapilas (abierta por un lado) y la lámina metálica de contacto.
  const wall = new THREE.Shape();
  wall.absarc(0, 0, 11.4, 0.5, Math.PI * 2 - 0.5, false);
  wall.absarc(0, 0, 10.5, Math.PI * 2 - 0.5, 0.5, true);
  const wallGeo = new THREE.ExtrudeGeometry(wall, { depth: pcbY - 0.2, bevelEnabled: false, curveSegments: 32 });
  wallGeo.rotateX(-Math.PI / 2);
  b.add(wallGeo, holder, { x, y: 0, z, ry: Math.PI });
  b.box(20, 0.3, 4.5, M.steel, x, pcbY - 1.1, z);
  if (battery) {
    b.cyl(10, 10, 3.2, mat('#c4c7cc', { roughness: 0.25, metalness: 0.95 }), x, 0.6, z, 40);
    b.cyl(8.6, 8.6, 0.05, mat('#d5d7db', { roughness: 0.2, metalness: 1 }), x, 3.8, z, 40);
  }
}

// DS1307 (Tiny RTC) ---------------------------------------------------------------------------------
function buildDs1307(def, props) {
  const D = DS1307_BOARD;
  const b = new Builder();
  const top = D.top;
  const p1z = DS1307_P1.map((_, i) => (i - 3) * PITCH);
  const p2 = ['DS', 'SCL', 'SDA', 'VCC', 'GND'];
  const p2z = p2.map((_, i) => (i - 2) * PITCH);
  pcb(b, {
    key: 'tinyrtc', w: D.w, d: D.d, y0: D.pcbY, color: '#1e57ad', edge: '#174488', scale: 24,
    draw: (g, X, Y) => {
      const label1 = { SQW: 'SQ' };
      DS1307_P1.forEach((n, i) => text(g, label1[n] || n, X(D.p1X + 2.2), Y(p1z[i]), { size: 1.15, color: SILK, align: 'left', weight: 700 }));
      p2.forEach((n, i) => text(g, n, X(D.p2X - 2.0), Y(p2z[i]), { size: 1.15, color: SILK, align: 'right', weight: 700 }));
      text(g, 'P1', X(D.p1X), Y(-11.6), { size: 1.2, color: SILK, weight: 700 });
      text(g, 'P2', X(D.p2X), Y(-8.6), { size: 1.2, color: SILK, weight: 700 });
      text(g, 'Tiny RTC', X(0), Y(11.6), { size: 2.0, color: SILK, weight: 800 });
      text(g, 'I2C modules', X(0), Y(-12.2), { size: 1.0, color: SILK, weight: 600 });
    },
  });
  // P1: solo se sueldan SQ, SCL, SDA, VCC y GND; DS y BAT quedan como agujeros.
  const pinsP1 = ['SQW', 'SCL', 'SDA', 'VCC', 'GND'];
  headerUp(b, [[D.p1X, p1z[0]]], { y: top, tipY: D.pinY });
  headerUp(b, pinsP1.slice(1).map((n) => [D.p1X, p1z[DS1307_P1.indexOf(n)]]), { y: top, tipY: D.pinY });
  for (const n of ['DS', 'BAT']) pad(b, D.p1X, p1z[DS1307_P1.indexOf(n)], top, { r: 0.95, hole: 0.5 });
  for (const z of p2z) pad(b, D.p2X, z, top, { r: 0.95, hole: 0.5 });
  // DS1307, EEPROM 24C32, cristal de 32.768 kHz, resistencias, capacitor y diodo de carga
  soic(b, b.root, -0.6, -5.6, top, { pins: 8, w: 4.9, d: 3.9, lines: ['DS1307Z', '1823C4'] });
  soic(b, b.root, -0.6, 3.4, top, { pins: 8, w: 4.9, d: 3.9, lines: ['ATMEL', '24C32'] });
  b.cylX(0.95, 6.2, M.steel, 4.6, top + 1.0, -9.6, 16);
  b.box(1.2, 0.5, 0.6, M.tin, 1.0, top, -9.6);
  const rs = [[5.6, -4.8, Math.PI / 2], [7.4, -4.8, Math.PI / 2], [5.6, 0.2, Math.PI / 2], [7.4, 0.2, Math.PI / 2], [5.6, 5.4, Math.PI / 2], [-5.8, -9.8, 0], [-5.8, 8.6, 0]];
  rs.forEach(([x, z, r], i) => smd(b, x, z, top, { rot: r, color: i === 6 ? '#b59a72' : '#1b1b1d' }));
  b.cylX(0.75, 3.4, mat('#d9782f', { roughness: 0.3 }), 7.2, top + 0.75, 9.4, 12); // diodo de vidrio
  b.box(0.5, 1.5, 1.5, M.chip, 8.4, top, 9.4);
  coinCell(b, 0, 0.8, D.pcbY, props.battery !== false);
  b.build();
  return { root: b.root };
}

// DS3231 (ZS-042) -----------------------------------------------------------------------------------
function buildDs3231(def, props) {
  const D = DS3231_BOARD;
  const b = new Builder();
  const top = D.top;
  const left = ['32K', 'SQW', 'SCL', 'SDA', 'VCC', 'GND'];
  const right = ['SCL', 'SDA', 'VCC', 'GND'];
  const lz = left.map((_, i) => (i - 2.5) * PITCH);
  const rz = right.map((_, i) => (i - 1.5) * PITCH);
  pcb(b, {
    key: 'zs042', w: D.w, d: D.d, y0: D.pcbY, color: '#1d58b2', edge: '#16458c', scale: 22,
    holes: [[13.6, -8.2, 1.4]],
    draw: (g, X, Y) => {
      left.forEach((n, i) => text(g, n, X(-16.7), Y(lz[i]), { size: 1.15, color: SILK, align: 'left', weight: 700 }));
      right.forEach((n, i) => text(g, n, X(16.7), Y(rz[i]), { size: 1.15, color: SILK, align: 'right', weight: 700 }));
      text(g, 'ZS-042', X(-2.4), Y(8.8), { size: 1.3, color: SILK, weight: 700 });
      ['A0', 'A1', 'A2'].forEach((n, i) => text(g, n, X(4.9 + i * 2.6), Y(-9.6), { size: 0.85, color: SILK }));
      text(g, 'Power', X(12.0), Y(9.4), { size: 0.85, color: SILK });
    },
  });
  headerSide(b, lz, { bendX: -17.4, tipX: -D.pinX, y: top + 1.27, top, dir: -1 });
  headerSide(b, rz, { bendX: 17.4, tipX: D.pinX, y: top + 1.27, top, dir: 1 });
  // DS3231SN (SOIC-16 ancho), EEPROM AT24C32, red de resistencias de 4.7 kΩ y componentes del cargador.
  soic(b, b.root, -3.2, -1.2, top, { pins: 16, w: 10.3, d: 7.5, h: 2.3, lines: ['DS3231SN', '2238A3 +'], wide: true });
  soic(b, b.root, 8.6, -2.6, top, { pins: 8, w: 4.9, d: 3.9, lines: ['ATMLH', '24C32'], rot: Math.PI / 2 });
  b.box(3.2, 0.55, 1.6, M.chip, -12.6, top, -0.6, { ry: Math.PI / 2 });
  for (let i = 0; i < 4; i++) for (const s of [-1, 1]) b.box(0.45, 0.56, 0.35, M.tin, -12.6 + s * 0.95, top, -0.6 - 1.2 + i * 0.8);
  for (let i = 0; i < 3; i++) for (const s of [-1, 1]) b.box(0.7, 0.06, 0.9, M.tin, 4.9 + i * 2.6 + s * 0.5, top, -8.2);
  smd(b, 4.4, 6.6, top, { rot: 0 });
  smd(b, 7.6, 6.6, top, { rot: 0, color: '#b59a72' });
  smd(b, -9.8, 6.4, top, { rot: 0, color: '#b59a72' });
  smd(b, 13.8, 6.6, top, { rot: Math.PI / 2 });
  b.box(2.6, 1.0, 1.4, mat('#2c2c2f', { roughness: 0.5 }), 1.0, top, 6.6); // diodo del cargador
  coinCell(b, 4.0, 0, D.pcbY, props.battery !== false);
  b.build();
  const led = powerLed(b.root, 12.0, 7.8, top, '#ff5a4a', '#ff2a14');
  led(false);
  return {
    root: b.root,
    update(v) {
      led(!!v.on);
    },
    reset() {
      led(false);
    },
  };
}

export const MODELS = {
  mpu6050: buildMpu6050,
  ds1307: buildDs1307,
  ds3231: buildDs3231,
};
